import { useNuxtApp, useRoute, useRuntimeConfig } from '#imports'
import { type Ref, onUnmounted, watch } from 'vue'

import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'

import { useShoppingAssistantStructuredDataRegistry } from '~/scopes/shopping-assistant/composables/useShoppingAssistantStructuredData'

import { BROWSING_CONTEXT_DEBOUNCE_MS } from '../constants'
import { useShoppingAssistantWebSidebar } from '../stores/useShoppingAssistantWebSidebar'

import { useShoppingAssistantApi } from './useShoppingAssistantApi'
import {
  type ShoppingAssistantApiOutcome,
  useShoppingAssistantObservability,
} from './useShoppingAssistantObservability'

/**
 * Pushes the current page's structured data as live browsing context while
 * the web sidebar is open, so the assistant knows what the user is looking
 * at. Feature-flagged; web-only, the full-page/webview assistant is
 * untouched.
 *
 * `chatId` is null until the web host's chat finishes (re-)creating; pushes
 * are buffered (skipped, kept pending) until it resolves, then the first
 * available registry snapshot flushes immediately.
 */
export function useShoppingAssistantBrowsingContext(
  chatId: Readonly<Ref<string | null>>,
) {
  const config = useRuntimeConfig()
  const store = useShoppingAssistantWebSidebar()
  const route = useRoute()
  const nuxtApp = useNuxtApp()
  const logger = useLogger()
  const { registry, reset } = useShoppingAssistantStructuredDataRegistry()
  const { pushBrowsingContext, abortBrowsingContextPush } =
    useShoppingAssistantApi()
  // Browsing context push is web-sidebar-only (see the doc comment above).
  const { recordApiCall } = useShoppingAssistantObservability(
    'web-sidebar',
    () => store.entryPoint ?? 'floating_button',
  )

  let debounceTimer: ReturnType<typeof setTimeout> | null = null

  function isEnabled(): boolean {
    return (
      Boolean(config.public.FF_SHOPPING_ASSISTANT_BROWSING_CONTEXT) &&
      store.isOpen
    )
  }

  async function flush() {
    if (!isEnabled()) return

    const id = chatId.value
    // No chat yet (still being created) — stay pending, the chatId watcher
    // below flushes as soon as one resolves.
    if (!id) return

    const startedAt = performance.now()
    // Captured now, not read live in the finally below: the sidebar can
    // close (resetting entryPoint) while this push is still in flight, which
    // would otherwise mislabel the measurement with the entry point active
    // at completion instead of the one active when the push was made.
    const entryPointAtStart = store.entryPoint ?? 'floating_button'
    let outcome: ShoppingAssistantApiOutcome = 'completed'
    let httpStatus: number | undefined
    let xRequestId: string | null = null

    try {
      await pushBrowsingContext(
        id,
        {
          url: route.fullPath,
          structured_data: Object.values(registry.value),
        },
        (requestId) => {
          xRequestId = requestId
        },
      )
    } catch (error) {
      if ((error as { name?: string }).name === 'AbortError') {
        outcome = 'aborted'
      } else {
        outcome = 'errored'
        httpStatus = (error as { status?: number }).status
        logger.error('[SHOPPING_ASSISTANT] Failed to push browsing context', {
          error: error as Error,
          owners: ['bot-group-ai-shopping-assistant-front'],
          chatId: id,
        })
      }
    } finally {
      recordApiCall({
        call: 'push_browsing_context',
        outcome,
        durationMs: performance.now() - startedAt,
        chatId: id,
        xRequestId,
        httpStatus,
        entryPoint: entryPointAtStart,
      })
    }
  }

  function scheduleFlush() {
    if (!isEnabled()) return

    if (debounceTimer) clearTimeout(debounceTimer)
    debounceTimer = setTimeout(flush, BROWSING_CONTEXT_DEBOUNCE_MS)
  }

  // A page (re-)declaring structured data reschedules the debounce, so a
  // reactive useHead getter re-firing shortly after page:finish (e.g. the
  // PDP's multi-ref Product schema) still lands its final version.
  const stopRegistryWatch = watch(registry, scheduleFlush)

  // Navigation settle signal: schedules a flush once the incoming route's
  // blocking async setup has resolved via Suspense.
  const unhookPageFinish = nuxtApp.hook('page:finish', scheduleFlush)
  // New navigation started: clear stale context so a slow-to-declare page
  // never inherits the previous page's structured data.
  const unhookPageStart = nuxtApp.hook('page:start', reset)

  // chatId resolves (panel just opened, or a fresh chat replaced the old
  // one): flush immediately with whatever is currently registered, so the
  // page already being viewed is never missed and the pre-chatId race
  // resolves as soon as possible.
  const stopChatIdWatch = watch(chatId, (id) => {
    if (id) flush()
  })

  onUnmounted(() => {
    if (debounceTimer) clearTimeout(debounceTimer)
    stopRegistryWatch()
    stopChatIdWatch()
    unhookPageFinish()
    unhookPageStart()
    abortBrowsingContextPush()
  })
}
